/**
 * Overview Tab Component
 * Displays summary stats, word cloud, and charts
 */

import { Grid } from '@mui/material'

import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import StatCard from 'modules/Cards/StatisticsCards/StatCard'
import HorizontalBarChart from 'modules/Charts/BarCharts/HorizontalBarChart'
import VerticalBarChart from 'modules/Charts/BarCharts/VerticalBarChart'
import WordCloud from 'modules/Charts/WordCloud'

import { OverviewTabProps } from './types'

// Format date for display
const formatDate = (dateString?: string) => {
	if (!dateString) return 'N/A'
	return new Date(dateString).toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	})
}

function OverviewTab({
	analysisData,
	wordChartData,
	phraseChartData,
}: OverviewTabProps): JSX.Element {
	return (
		<Grid container spacing={3}>
			{/* Stats Cards */}
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard
							title="Total Calls Analysed"
							value={analysisData?.totalCalls || 0}
							icon="phone"
							color="info"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard
							title="Total Transcripts"
							value={analysisData?.totalTranscripts || 0}
							icon="description"
							color="success"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard
							title="Total Words"
							value={analysisData?.totalWords?.toLocaleString() || 0}
							icon="text_fields"
							color="primary"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard
							title="Analysis Period"
							value={`${formatDate(analysisData?.analysedPeriod?.start)} - ${formatDate(
								analysisData?.analysedPeriod?.end
							)}`}
							icon="date_range"
							color="warning"
						/>
					</Grid>
				</Grid>
			</Grid>

			{/* Word Cloud */}
			<Grid item xs={12}>
				<MDBox>
					<MDTypography variant="h6" mb={1}>
						Word Cloud
					</MDTypography>
					<MDTypography variant="caption" color="text" mb={2}>
						Most frequently used words - size indicates frequency
					</MDTypography>
					{analysisData?.charts?.wordCloud && <WordCloud words={analysisData.charts.wordCloud} />}
				</MDBox>
			</Grid>

			{/* Charts */}
			<Grid item xs={12} md={6}>
				<VerticalBarChart
					title="Top 10 Words"
					description="Most frequently used words in transcripts"
					height="300px"
					chart={wordChartData}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<HorizontalBarChart
					title="Top Phrases"
					description="Most common multi-word phrases"
					height="300px"
					chart={phraseChartData}
				/>
			</Grid>
		</Grid>
	)
}

export default OverviewTab
